/**
 * SkyModule — IModule for the Hillaire-style physically-based sky.
 *
 * Sets up:
 *   - `SkyRenderer` (inline ray-march sky TSL node)
 *   - `scene.backgroundNode` bound to the sky
 *   - `SkyEnvironmentBaker` that periodically renders the sky into a
 *     PMREM-prefiltered IBL cube and assigns it to `scene.environment`
 *   - Sun-direction tracking (configurable; default reads from provided
 *     callback; otherwise stays at `(0, 1, 0)` zenith)
 *
 * The environment bake is throttled — re-runs only when the sun direction
 * moves by more than `envBakeSunThreshold` radians (default ~1°) or the
 * atmosphere config changes.
 *
 * Usage:
 *   app.use(new SkyModule({
 *     sunDirectionProvider: () => dayNight.getSunDirection(),
 *   }));
 *
 * Declarative (requires HtmlEcsModule):
 *   <x-sky comp="sky.sun=0,1,0; sky.sunIlluminance=20"></x-sky>
 */

import type { App, IModule } from '../../core/App.js';
import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import * as THREE from 'three/webgpu';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import { Logger } from '../../core/Logger.js';
import { SkyRenderer } from './SkyRenderer.js';
import { SkyEnvironmentBaker } from './SkyEnvironmentBaker.js';
import {
  createAtmosphereConfig, atmosphereConfigKey, type AtmosphereConfig,
} from './AtmosphereConfig.js';
import { defineXSky, registerSkyHtml } from './html/index.js';

export interface SkyModuleConfig {
  /** Custom atmosphere parameters. Defaults to Earth-at-sea-level. */
  atmosphere?: Partial<AtmosphereConfig>;
  /**
   * Cube resolution for the environment bake. 64/128/256. Default 128.
   * Larger values cost more per bake but give sharper reflections.
   */
  envResolution?: number;
  /**
   * Angular threshold (radians) — the sun must move by more than this
   * before the environment re-bakes. Default 0.02 (~1.15°).
   */
  envBakeSunThreshold?: number;
  /**
   * Force the environment to re-bake at least this often (seconds). Useful
   * when other scene changes should influence IBL. Default 5 seconds.
   */
  envBakeMaxInterval?: number;
  /**
   * Optional provider for the sun direction (world-space unit vec FROM
   * origin TO sun). Called every frame. If null, sun stays at the last
   * value set via `setSunDirection()`.
   */
  sunDirectionProvider?: () => { x: number; y: number; z: number };
  /** Initial sun illuminance multiplier. Default 20. */
  sunIlluminance?: number;
  /**
   * Camera altitude in km (radius from planet center). The default
   * (bottomRadius + 1 m) places the camera just above ground. Set higher
   * for aerial / space scenarios.
   */
  cameraAltitudeKm?: number;
  /**
   * Track the camera's world Y into the sky observer altitude every frame
   * (meters → km). Default true. Set FALSE for ground-scale games with tall
   * terrain: the real sky barely changes over a few hundred meters, but the
   * tracked LUT observer height re-bakes the sky-view LUT in discrete
   * altitude steps — the sky visibly "pops" as you climb a hill and each
   * re-bake costs CPU mid-traversal. When false the altitude stays at
   * `cameraAltitudeKm` (or the just-above-ground default) forever.
   */
  trackCameraAltitude?: boolean;
  /**
   * THE AERIAL ALTITUDE (2026-10-06): bake the aerial-perspective froxel volume from the camera's REAL altitude even when
   * `trackCameraAltitude` is false. The volume clips every ray at the planet's ground, so a volume baked from the
   * just-above-ground default clipped every ray below the horizon within metres — no aerial haze on terrain at any
   * distance. It re-bakes the volume alone, past `aerialAltitudeThresholdKm` (25 m), never the sky-view. Default true;
   * false keeps the old behaviour.
   */
  trackAerialAltitude?: boolean;
  /**
   * Sky evaluation pipeline:
   *   'inline' — v1 per-fragment ray-march (DEFAULT — zero-risk drop-in).
   *   'lut'    — v2 Hillaire 4-LUT path (samples a baked sky-view LUT;
   *              ~10× cheaper per fragment; CPU-bakes on sun/atmosphere
   *              change). Faithful to Hillaire 2020.
   *   'auto'   — 'lut' when the renderer is WebGPU, else 'inline'.
   * Default 'inline'.
   */
  pipeline?: 'inline' | 'lut' | 'auto';
  /**
   * Bake the aerial-perspective froxel volume (v2 LUT path only). The volume
   * + `apSample()` helper are produced, but compositing into the post chain
   * is DEFERRED (see render-sky.md). Default false.
   */
  enableAerialPerspective?: boolean;
  /** The aerial volume's frame from the first bake (SkyRenderer `aerialFrame`; default 'screen'). */
  aerialFrame?: 'screen' | 'world';
  /**
   * The inline pipeline's LUT parity (SkyRenderer `lutParity`, 2026-09-26):
   * the per-fragment march adds Hillaire's multiple-scattering term and the
   * analytic sun disc so a WebGL2 dome ('auto' → 'inline') matches the WebGPU
   * LUT dome. Ignored on the LUT pipeline. Default false.
   */
  lutParity?: boolean;
}

/** The environment bake driver's state (`SkyModule.envBakeState`). */
export interface EnvBakeState {
  /** An amortized cycle is in flight. */
  baking: boolean;
  /** Its step (0..5 faces, 6 the PMREM base, 7 the GGX levels). */
  step: number;
  /** One-shot bakes run, cycles restarted, starts the gate deferred, staged cycles completed. */
  immediateBakes: number;
  restarts: number;
  gateDeferrals: number;
  cycles: number;
}

/** A refusing bake gate is overruled after this many consecutive frames — the cube never starves (the latch law). */
export const ENV_BAKE_GATE_MAX_DEFER = 30;

export class SkyModule implements IModule {
  readonly name = 'Sky';

  private _config: SkyModuleConfig;
  private _atmosphere: AtmosphereConfig;
  private _atmosphereKey: string;
  private _renderer: SkyRenderer | null = null;
  private _baker: SkyEnvironmentBaker | null = null;
  private _system: SkyRuntimeSystem | null = null;

  constructor(config: SkyModuleConfig = {}) {
    this._config = config;
    this._atmosphere = createAtmosphereConfig(config.atmosphere);
    this._atmosphereKey = atmosphereConfigKey(this._atmosphere);
  }

  get renderer(): SkyRenderer {
    if (!this._renderer) throw new Error('SkyModule: not initialized');
    return this._renderer;
  }

  get baker(): SkyEnvironmentBaker {
    if (!this._baker) throw new Error('SkyModule: not initialized');
    return this._baker;
  }

  get atmosphere(): AtmosphereConfig { return this._atmosphere; }

  /**
   * The baked aerial-perspective froxel texture, or null when the LUT
   * pipeline is off / `enableAerialPerspective` is false / not yet baked.
   * Feed it to PostProcessingPipeline's `aerial` config (identity-stable
   * across re-bakes — see SkyLutSet, so the post chain never rebuilds).
   */
  get aerialTexture(): THREE.Data3DTexture | null {
    return this._renderer?.lutSet?.aerial?.texture ?? null;
  }

  /** Set new atmosphere parameters. Forces an environment re-bake next frame. */
  setAtmosphere(cfg: AtmosphereConfig): void {
    this._atmosphere = cfg;
    this._atmosphereKey = atmosphereConfigKey(cfg);
    this._renderer?.setAtmosphere(cfg);
    this._system?.forceBakeNextFrame();
  }

  /** Update the sun direction (world space, FROM origin TO sun). */
  setSunDirection(dir: { x: number; y: number; z: number }): void {
    this._renderer?.setSunDirection(dir);
    this._system?.onSunUpdated(dir);
  }

  /** Update the sun illuminance multiplier. */
  setSunIlluminance(v: number): void {
    this._renderer?.setSunIlluminance(v);
  }

  /**
   * Re-bake the environment cube at the next frame (the caller changed what the
   * bake renders — e.g. a weather bucket through SkyRenderer.setBakeOverrides).
   * The bake is amortized one face per frame, so this is a ~8-frame cycle.
   */
  requestEnvBake(): void {
    this._system?.forceBakeNextFrame();
  }

  /**
   * THE LATCH LAW (2026-09-27). A cycle latches the bake overrides at its FIRST face and the live environment only
   * answers to them once the cycle completes, ~15 frames later (seconds on a slow renderer). A caller whose sky can
   * jump (a time-of-day pin, a weather snap, a world entry) steers the bake with these three levers:
   *
   * - `setEnvBakeGate(fn)`: consulted before a new cycle STARTS (never mid-cycle). `false` keeps the bake pending — so
   *   the latch is taken from a settled frame, not the one between a time jump and the weather catching up. A gate
   *   that keeps refusing is overruled after `ENV_BAKE_GATE_MAX_DEFER` frames, so the cube can never starve.
   * - `restartEnvBake()`: abandon the cycle in flight (its latch is stale) and start a fresh one when allowed.
   * - `requestImmediateEnvBake()`: at this module's next frame (after the sun and the LUTs are current) bake the whole
   *   cube and its PMREM chain in ONE frame, latch and complete together. The staged cycle writes the live PMREM target
   *   level by level while the latch still names the previous cube, so across a large change the sharp levels show the
   *   new sky × the old carrier (a whitened sea after a reload) — the one-shot is atomic. It costs one frame's hitch:
   *   for regime changes (a world entry, a jump), never routine drift.
   */
  setEnvBakeGate(gate: (() => boolean) | null): void { this._system?.setGate(gate); }
  restartEnvBake(): void { this._system?.restartBake(); }
  requestImmediateEnvBake(): void { this._system?.requestImmediate(); }
  /** The bake driver's state (a cycle in flight and its step; counters for diagnostics). One object, refilled per read. */
  get envBakeState(): EnvBakeState {
    return this._system?.state() ?? { baking: false, step: 0, immediateBakes: 0, restarts: 0, gateDeferrals: 0, cycles: 0 };
  }

  /** Camera altitude in km from planet center. */
  setCameraAltitudeKm(r: number): void {
    this._renderer?.setCameraAltitudeKm(r);
  }

  init(app: App): void {
    // Resolve the pipeline. 'auto' picks 'lut' on WebGPU (where the LUT
    // sky-view fetch is cheapest) and 'inline' otherwise. The LUT bake is
    // pure CPU, so 'lut' works on WebGL2 too — 'auto' is just a sensible
    // default that keeps the WebGL2 fallback on the simpler v1 path.
    const requested = this._config.pipeline ?? 'inline';
    const resolvedPipeline: 'inline' | 'lut' =
      requested === 'auto'
        ? (app.renderer.isWebGPU ? 'lut' : 'inline')
        : requested;

    // The LUT path uploads float DataTextures with LinearFilter. On WebGL2,
    // linear filtering of float textures needs the OES_texture_float_linear
    // extension, which a few (mostly mobile) GPUs lack — there sky-view sampling
    // silently falls back to nearest filtering (banding). We do NOT change the
    // texture formats (precision tests pin them); we just warn once so the
    // requirement is visible. (No-op on WebGPU, where float-linear is core.)
    if (resolvedPipeline === 'lut' && !app.renderer.isWebGPU) {
      Logger.warn('Sky',
        'LUT pipeline on WebGL2: float-LUT linear filtering requires the ' +
        'OES_texture_float_linear extension. Without it, sky-view/aerial LUT ' +
        'sampling falls back to nearest (banding). See render-sky.md Pitfalls.');
    }

    this._renderer = new SkyRenderer({
      atmosphere: this._atmosphere,
      sunIlluminance: this._config.sunIlluminance,
      pipeline: resolvedPipeline,
      enableAerialPerspective: this._config.enableAerialPerspective,
      aerialFrame: this._config.aerialFrame,
      lutParity: this._config.lutParity,
    });
    this._renderer.setCameraAltitudeKm(
      this._config.cameraAltitudeKm ?? (this._atmosphere.bottomRadius + 0.001),
    );

    // Assign to scene.backgroundNode so Three.js renders the sky as the
    // infinity background of the scene.
    const sceneAny = app.world.scene as unknown as { backgroundNode?: unknown };
    sceneAny.backgroundNode = this._renderer.buildBackgroundNode();
    // Clearing scene.background ensures the NodeMaterial path wins.
    (app.world.scene as THREE.Scene).background = null;

    this._baker = new SkyEnvironmentBaker({
      renderer: app.renderer.native,
      skyRenderer: this._renderer,
      resolution: this._config.envResolution,
    });

    // Register runtime system that handles sun updates + env bake triggers.
    this._system = new SkyRuntimeSystem({
      app,
      module: this,
      baker: this._baker,
      sunProvider: this._config.sunDirectionProvider ?? null,
      bakeSunThreshold: this._config.envBakeSunThreshold ?? 0.02,
      bakeMaxIntervalSeconds: this._config.envBakeMaxInterval ?? 5,
      trackCameraAltitude: this._config.trackCameraAltitude ?? true,
      trackAerialAltitude: this._config.trackAerialAltitude ?? true,
      getAtmosphereKey: () => this._atmosphereKey,
    });
    app.world.addSystem(this._system);

    defineXSky();
    const htmlModule = app.getModule('HtmlEcs') as
      | (IModule & { htmlRegistry?: unknown })
      | undefined;
    if (htmlModule && htmlModule.htmlRegistry) {
      registerSkyHtml(htmlModule.htmlRegistry as never, this);
      Logger.info('Sky', 'HTML integration wired (<x-sky> + sky.* comp keys)');
    }

    Logger.info('Sky', 'Module initialized (physically-based Rayleigh+Mie+ozone)');
  }

  dispose(): void {
    this._baker?.dispose();
    this._renderer?.dispose();
    this._baker = null;
    this._renderer = null;
    this._system = null;
    Logger.info('Sky', 'Module disposed');
  }
}

/**
 * Internal per-frame system that:
 *   - pulls sun direction from the provider (if any), updates SkyRenderer
 *   - triggers SkyEnvironmentBaker.bake() when sun moved enough or the
 *     atmosphere key changed
 *   - assigns the baked PMREM texture to scene.environment
 */
class SkyRuntimeSystem implements ISystem {
  readonly name = 'SkyRuntime';
  readonly phase = LoopPhase.RenderSub;
  readonly subPhase = SubPhase.Update;
  readonly priority = 6;

  private readonly _app: App;
  private readonly _module: SkyModule;
  private readonly _baker: SkyEnvironmentBaker;
  private readonly _sunProvider: (() => { x: number; y: number; z: number }) | null;
  private readonly _threshold: number;
  private readonly _maxInterval: number;
  private readonly _getAtmosphereKey: () => string;
  private readonly _trackAltitude: boolean;
  private readonly _trackAerialAltitude: boolean;

  private _lastSunDir = { x: 0, y: 1, z: 0 };
  private _lastBakeTime = -Infinity;
  private _lastAtmKey = '';
  private _hasBaked = false;
  private _forceNextBake = true;
  private _elapsedSeconds = 0;
  /** Amortized bake cycle in flight (one baker step per frame). */
  private _baking = false;
  /** Sun below astronomical twilight last frame (dark-sky bake damping). */
  private _wasDark = false;
  /** The latch law (2026-09-27): the start gate, its consecutive refusals, a pending one-shot, the LUT force edge. */
  private _gate: (() => boolean) | null = null;
  private _gateDeferred = 0;
  private _immediate = false;
  /**
   * The LUTs are forced ONCE when a bake is first asked for — they used to be forced on EVERY frame a bake stood
   * pending (`updateLuts(this._forceNextBake)`), so a request that landed mid-cycle re-baked tier A + B (+ the aerial
   * froxels) on each of the cycle's remaining frames. Sun / altitude / config changes re-bake them on their own.
   */
  private _lutForce = true;
  private _counters = { immediateBakes: 0, restarts: 0, gateDeferrals: 0, cycles: 0 };

  constructor(args: {
    app: App;
    module: SkyModule;
    baker: SkyEnvironmentBaker;
    sunProvider: (() => { x: number; y: number; z: number }) | null;
    bakeSunThreshold: number;
    bakeMaxIntervalSeconds: number;
    getAtmosphereKey: () => string;
    trackCameraAltitude: boolean;
    trackAerialAltitude?: boolean;
  }) {
    this._app = args.app;
    this._module = args.module;
    this._baker = args.baker;
    this._sunProvider = args.sunProvider;
    this._threshold = args.bakeSunThreshold;
    this._maxInterval = args.bakeMaxIntervalSeconds;
    this._getAtmosphereKey = args.getAtmosphereKey;
    this._trackAltitude = args.trackCameraAltitude;
    this._trackAerialAltitude = args.trackAerialAltitude ?? true;
  }

  forceBakeNextFrame(): void {
    this.raiseForce();
  }

  /** Ask for a bake; the LUTs are forced on the first frame of the ask only (see `_lutForce`). */
  private raiseForce(): void {
    if (!this._forceNextBake) this._lutForce = true;
    this._forceNextBake = true;
  }

  setGate(gate: (() => boolean) | null): void {
    this._gate = gate;
    this._gateDeferred = 0;
  }

  restartBake(): void {
    if (this._baking) {
      this._baker.resetCycle();
      this._baking = false;
      this._counters.restarts++;
    }
    this.raiseForce();
  }

  requestImmediate(): void {
    this._immediate = true;
  }

  /** Filled in place (read every frame by a caller — no per-frame allocation). */
  private readonly _stateOut: EnvBakeState = { baking: false, step: 0, immediateBakes: 0, restarts: 0, gateDeferrals: 0, cycles: 0 };
  state(): EnvBakeState {
    const o = this._stateOut, c = this._counters;
    o.baking = this._baking; o.step = this._baker.cycleStep;
    o.immediateBakes = c.immediateBakes; o.restarts = c.restarts; o.gateDeferrals = c.gateDeferrals; o.cycles = c.cycles;
    return o;
  }

  onSunUpdated(dir: { x: number; y: number; z: number }): void {
    // Mutate in place — this object is compared, never aliased, and the
    // per-frame literal was steady GC churn on the always-on sky path.
    this._lastSunDir.x = dir.x;
    this._lastSunDir.y = dir.y;
    this._lastSunDir.z = dir.z;
  }

  execute(dt: number, _world: World): void {
    this._elapsedSeconds += dt;

    if (this._sunProvider) {
      const dir = this._sunProvider();
      const prev = this._lastSunDir;
      const moved = 1 - (prev.x * dir.x + prev.y * dir.y + prev.z * dir.z);
      this._module.setSunDirection(dir);
      prev.x = dir.x; prev.y = dir.y; prev.z = dir.z;
      if (moved > this._threshold * this._threshold * 0.5 && dir.y >= DARK_SKY_SUN_Y) {
        // The dot-based drift uses a "cosine-derived" check; threshold² · 0.5
        // is the small-angle approximation of (1 - cos θ). Past astronomical
        // twilight (dark-sky damping below) drift changes nothing visible in
        // the env — the stretched interval carries the cycle instead.
        this.raiseForce();
      }
    }
    // Dark-sky damping: below astronomical twilight the sky env is static, so
    // the (PMREM-dominated) re-bake buys nothing — live-profiled 2026-07-31 as
    // the last recurring hitch during night play. One forced bake fires on
    // each dark-boundary crossing so dusk/dawn stay correct.
    const dark = this._lastSunDir.y < DARK_SKY_SUN_Y;
    if (dark !== this._wasDark) {
      this._wasDark = dark;
      this.raiseForce();
    }

    // Camera altitude tracking — read from first camera if present. Skipped
    // when trackCameraAltitude:false (ground-scale games with tall terrain:
    // the sky barely changes over hundreds of meters, but tracking re-bakes
    // the sky-view LUT in visible altitude steps as you climb).
    const cam = (this._app.renderer.cameras as unknown as {
      main?: THREE.Camera; getEntries?: () => Array<{ camera: THREE.Camera }>;
    });
    const camObj = this._trackAltitude || this._trackAerialAltitude ? (cam.main ?? cam.getEntries?.()[0]?.camera) : undefined;
    if (camObj) {
      const pos = camObj.getWorldPosition(_tmpPos);
      // Convert world meters above ground → radius from planet center in km.
      // We assume camera Y is altitude above ground (flat-earth local scene).
      // For a round-earth scene you'd use length(pos - planetCenter) * 0.001.
      const altKm = Math.max(0, pos.y * 0.001);
      const radiusKm = this._module.atmosphere.bottomRadius + altKm + 0.001;
      if (this._trackAltitude) this._module.setCameraAltitudeKm(radiusKm);
      // THE AERIAL ALTITUDE: the froxels from the camera's real height even when the sky-view holds at the ground.
      else this._module.renderer.setAerialAltitudeKm(radiusKm);
    }

    // Atmosphere-change detection
    const currentKey = this._getAtmosphereKey();
    if (currentKey !== this._lastAtmKey) {
      this._lastAtmKey = currentKey;
      this.raiseForce();
    }

    // v2 LUT path: regenerate dirty LUTs (sky-view re-bakes on sun/altitude
    // drift; transmittance/multi-scatter on atmosphere change). A re-bake
    // means the sky changed, so force a PMREM env re-bake to keep IBL in
    // sync. No-op in inline mode (returns null).
    // The latch law (2026-09-27): force the LUTs on the FIRST frame of an ask only (and for a one-shot, whose faces
    // must sample a sky-view LUT at this frame's exact sun); a pending ask re-forced them every frame of a cycle.
    const lutResult = this._module.renderer.updateLuts(this._lutForce || this._immediate);
    this._lutForce = false;
    if (lutResult && (lutResult.tierA || lutResult.tierB)) {
      this._forceNextBake = true; // the LUTs are current already — no second force edge
    }

    // THE ONE-SHOT (the latch law): the whole cube + PMREM chain in this frame, latched and completed together; any
    // cycle in flight is abandoned (its latch is stale). Asked for by the caller on a regime change.
    if (this._immediate) {
      this._immediate = false;
      this._baker.resetCycle();
      this._baking = false;
      if (this._baker.bake()) {
        const env = this._baker.output;
        if (env) (this._app.world.scene as THREE.Scene).environment = env;
        this._hasBaked = true;
        this._lastBakeTime = this._elapsedSeconds;
        this._forceNextBake = false;
        this._gateDeferred = 0;
        this._counters.immediateBakes++;
      } else {
        this.raiseForce(); // the staged driver retries
      }
      return;
    }

    // Bake driver — AMORTIZED (live-profiled 2026-07-31): the one-shot bake
    // was a recurring ~120 ms tick (6 cube faces + the PMREM chain in one
    // frame). One baker step per frame spreads the faces; only the PMREM
    // frame remains a (smaller) single-frame cost. Drift/atmosphere changes
    // during a cycle re-set _forceNextBake and queue the next cycle.
    if (this._baking) {
      const r = this._baker.bakeStep();
      if (r === 'done') {
        const env = this._baker.output;
        if (env) {
          (this._app.world.scene as THREE.Scene).environment = env;
        }
        this._hasBaked = true;
        this._lastBakeTime = this._elapsedSeconds;
        this._baking = false;
        this._counters.cycles++;
      } else if (r === 'failed') {
        this.raiseForce(); // retry a fresh cycle next frame
        this._baking = false;
      }
      return;
    }
    const effInterval = dark ? this._maxInterval * 8 : this._maxInterval;
    const intervalElapsed = (this._elapsedSeconds - this._lastBakeTime) > effInterval;
    const shouldBake = this._forceNextBake
                    || (this._hasBaked && intervalElapsed)
                    || !this._hasBaked;
    if (shouldBake) {
      // THE START GATE (the latch law): the first face latches the overrides, so a cycle starts only on a frame the
      // caller calls settled; the ask stays pending meanwhile. A gate that never opens is overruled.
      if (this._gate && !this._gate() && this._gateDeferred < ENV_BAKE_GATE_MAX_DEFER) {
        this._gateDeferred++;
        this._counters.gateDeferrals++;
        return;
      }
      this._gateDeferred = 0;
      this._forceNextBake = false;
      this._baking = true;
      // First face this frame — the LUT updates above already spent this
      // frame's sky budget, but a single 128² face render is small.
      if (this._baker.bakeStep() === 'failed') {
        this.raiseForce();
        this._baking = false;
      }
    }
  }

  dispose(): void {
    // SkyModule.dispose handles baker cleanup.
  }
}

const _tmpPos = new THREE.Vector3();

/** Sun-direction Y below which the sky counts as astronomically dark (≈ sun
 *  20° under the horizon — past the last twilight glow). */
const DARK_SKY_SUN_Y = -0.35;
